/**
 * dst-raceware-track — one continuous pinned scroll track for the Raceware
 * page's hero/LED-gallery/dashboard-gallery flow. Same idiom as
 * dst-about-track.js (About page) and dst-showcase-track.js (homepage): one
 * "reel" per chapter, only one visible at a time, fade in → show content →
 * fade out → next chapter fades in.
 *
 * The difference from dst-about-track.js: each chapter picks its own content
 * mode —
 *   'images' — up to 4 real photos/screenshots crossfade as the visitor
 *              scrolls through the chapter (identical mechanic to the About
 *              page's chapters)
 *   'video'  — a single looping, muted, no-controls <video> plays for the
 *              whole time the chapter is active; playback is time-based, not
 *              scroll-linked, so there's no per-tick index math for it — just
 *              play() when this chapter becomes active, pause() when it stops
 *              being active (saves resources on off-screen chapters).
 *
 * Bails out entirely on mobile / prefers-reduced-motion — CSS shows a
 * separate static stacked fallback instead. Same for <noscript>.
 */
class DstRacewareTrack extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(max-width: 749px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this._progressEl = this.querySelector('.dst-rwc__progress');
    this._currentReel = -1;
    this._activeImage = new Map(); // reel index -> currently active image index

    this._chapterEls = Array.from(this.querySelectorAll('.dst-rwc__chapter'));
    if (this._chapterEls.length === 0) return;

    // Chapters ship with the first one visible in the server-rendered HTML
    // purely as a no-JS fallback signal — strip that so JS has full control.
    this._chapterEls.forEach(el => el.classList.remove('is-active'));

    this._reels = this._chapterEls.map(el => {
      const mode = el.dataset.mode === 'video' ? 'video' : 'images';
      return {
        el,
        mode,
        images: mode === 'images' ? Array.from(el.querySelectorAll('.dst-rwc__media-image')) : [],
        videoEl: mode === 'video' ? el.querySelector('.dst-rwc__media-video') : null,
        fadeVh:    parseFloat(el.dataset.fadeVh) || 50,
        contentVh: parseFloat(el.dataset.contentVh) || 150,
      };
    });

    this.classList.add('dst-rwc--js-ready');

    this._scrollGate = new DstScrollUtils.ScrollGate(this, () => this._onScrollRAF());
    this._onScrollRAF(); // correct state immediately if scroll position was restored

    window.addEventListener('resize', () => this._onScrollRAF(), { passive: true });
  }

  disconnectedCallback() {
    if (this._scrollGate) this._scrollGate.destroy();
  }

  _reelTotalVh(reel) {
    return reel.fadeVh * 2 + reel.contentVh;
  }

  _setActiveReel(reelIndex) {
    if (reelIndex === this._currentReel) return;

    const previous = this._reels[this._currentReel];
    if (previous && previous.mode === 'video' && previous.videoEl) {
      previous.videoEl.pause();
    }

    this._currentReel = reelIndex;
    this._chapterEls.forEach((el, i) => el.classList.toggle('is-active', i === reelIndex));

    const current = this._reels[reelIndex];
    if (current && current.mode === 'video' && current.videoEl) {
      const playPromise = current.videoEl.play();
      if (playPromise && playPromise.catch) playPromise.catch(() => {}); // autoplay can be blocked — ignore
    }
  }

  _onScrollRAF() {
    const scrollable = this.offsetHeight - window.innerHeight;
    if (scrollable <= 0) return;

    const scrolledPx = Math.max(0, Math.min(scrollable, -this.getBoundingClientRect().top));

    if (this._progressEl) {
      this._progressEl.style.width = `${(scrolledPx / scrollable) * 100}%`;
    }

    const totalVh = this._reels.reduce((sum, r) => sum + this._reelTotalVh(r), 0) || 1;

    let reelIndex = 0;
    let reelLocalPx = 0;
    let reelTrackPx = 0;
    let acc = 0;
    for (let i = 0; i < this._reels.length; i++) {
      const rVh = this._reelTotalVh(this._reels[i]);
      const rPx = scrollable * (rVh / totalVh);
      if (scrolledPx <= acc + rPx || i === this._reels.length - 1) {
        reelIndex = i;
        reelTrackPx = rPx;
        reelLocalPx = Math.max(0, Math.min(rPx, scrolledPx - acc));
        break;
      }
      acc += rPx;
    }

    const reel = this._reels[reelIndex];
    const reelVh = this._reelTotalVh(reel);
    const fadePx  = reelTrackPx * (reel.fadeVh / reelVh);
    const contentPx = reelTrackPx * (reel.contentVh / reelVh);

    // First chapter skips its fade-in — starts already fully visible, so
    // there's something on screen at scroll position 0 instead of black.
    // Matches the homepage showcase's handling of its first product.
    const b1 = reelIndex === 0 ? 0 : fadePx;
    const b2 = b1 + contentPx;

    let opacity;
    let contentProgress;

    if (reelLocalPx <= b1) {
      opacity = (reelIndex === 0 || fadePx <= 0) ? 1 : reelLocalPx / fadePx;
      contentProgress = 0;
    } else if (reelLocalPx <= b2) {
      opacity = 1;
      contentProgress = contentPx > 0 ? (reelLocalPx - b1) / contentPx : 0;
    } else {
      const fadeOutPx = reelTrackPx - b2;
      const p = fadeOutPx > 0 ? (reelLocalPx - b2) / fadeOutPx : 1;
      opacity = 1 - Math.max(0, Math.min(1, p));
      contentProgress = 1;
    }

    // Only the active reel is shown; every other reel is fully hidden.
    this._reels.forEach((r, i) => {
      r.el.style.opacity = i === reelIndex ? String(Math.max(0, Math.min(1, opacity))) : '0';
    });

    this._setActiveReel(reelIndex);

    if (reel.mode === 'images' && reel.images.length > 0) {
      const imageIndex = Math.min(
        reel.images.length - 1,
        Math.floor(Math.max(0, Math.min(0.9999, contentProgress)) * reel.images.length)
      );
      if (this._activeImage.get(reelIndex) !== imageIndex) {
        this._activeImage.set(reelIndex, imageIndex);
        reel.images.forEach((img, i) => img.classList.toggle('is-active', i === imageIndex));
      }
    }
  }
}

customElements.define('dst-raceware-track', DstRacewareTrack);
